import { HRM_ONBOARDING_DOCUMENTS } from './HRMModuleData'
import { formatValueToISODate, formatValueToMMDDYYYY, parseUSDateValue } from '../../../components/shared/customDateFormatUtils'

export const safeJsonParse = (value, fallback = []) => {
  if (value === null || value === undefined || value === '') {
    return fallback
  }

  if (Array.isArray(value)) {
    return value
  }

  if (typeof value === 'object') {
    return value
  }

  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

export const parseProviderListResponse = (response) => {
  const outer = safeJsonParse(response?.d ?? response, {})
  const inner = safeJsonParse(outer?.d ?? outer?.Data ?? outer?.data ?? outer, {})

  if (Array.isArray(inner)) return inner
  if (Array.isArray(inner?.Data)) return inner.Data
  if (Array.isArray(inner?.data)) return inner.data
  if (Array.isArray(outer?.Data)) return outer.Data
  if (Array.isArray(outer?.data)) return outer.data
  return []
}

export const toInputDate = (value) => {
  if (!value) return ''
  return formatValueToMMDDYYYY(value)
}

export const formatDate = (value) => {
  if (!value) return '-'
  return formatValueToMMDDYYYY(value) || '-'
}

export const toStorageDate = (value) => formatValueToISODate(value)

export const parseHRMSafeDate = (value) => parseUSDateValue(value)

export const getProviderName = (provider = {}) => {
  return provider?.Name || provider?.ProviderName || provider?.FullName || provider?.Username || 'Unnamed provider'
}

export const normalizeHRMDocuments = (documents) => {
  const emptyDocuments = {
    medicalLicense: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    driversLicense: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    dea: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    resumeCv: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    collaborativeAgreement: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    npdb: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    degreeCertificate: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    aanpAancCertificate: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    malpractice: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    covidCertificate: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    fluTest: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    tbTest: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
    passportPhoto: { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' },
  }

  const parsedDocuments = safeJsonParse(documents, documents && typeof documents === 'object' ? documents : {})

  if (Array.isArray(parsedDocuments)) {
    const orderedKeys = Object.keys(emptyDocuments)
    parsedDocuments.forEach((doc, index) => {
      const key = doc?.documentKey || doc?.key || doc?.DocumentType || doc?.documentType || orderedKeys[index] || ''
      if (!key) return

      const target = emptyDocuments[key] || { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' }
      emptyDocuments[key] = {
        ...target,
        id: doc?.id || doc?.Id || doc?.ID || '',
        documentId: doc?.documentId || doc?.DocumentId || doc?.Id || doc?.ID || '',
        providerId: doc?.providerId || doc?.ProviderId || doc?.ProviderID || '',
        documentKey: key,
        customName: doc?.customName || doc?.CustomName || doc?.DocumentName || doc?.documentName || doc?.DocumentLabel || doc?.documentLabel || doc?.Label || doc?.label || '',
        documentLabel:
          doc?.customName ||
          doc?.CustomName ||
          doc?.DocumentName ||
          doc?.documentName ||
          doc?.DocumentLabel ||
          doc?.documentLabel ||
          doc?.Label ||
          doc?.label ||
          doc?.documentLabel ||
          HRM_ONBOARDING_DOCUMENTS.find((item) => item.key === key)?.label ||
          '',
        fileName: doc?.fileName || doc?.FileName || doc?.filename || '',
        fileType: doc?.fileType || doc?.FileType || doc?.filetype || '',
        fileSize: doc?.fileSize || doc?.FileSize || doc?.filesize || '',
        expiryDate: toInputDate(doc?.expiryDate || doc?.ExpiryDate || ''),
        previewUrl: doc?.previewUrl || doc?.url || doc?.secure_url || doc?.FilePath || doc?.filePath || doc?.filepath || '',
        previewToken: doc?.previewToken || '',
        publicId: doc?.publicId || doc?.public_id || '',
        assetId: doc?.assetId || doc?.asset_id || '',
        documentStatus: doc?.documentStatus || doc?.DocumentStatus || '',
      }
    })

    return emptyDocuments
  }

  if (!parsedDocuments || typeof parsedDocuments !== 'object') {
    return emptyDocuments
  }

  const allKeys = new Set([...Object.keys(emptyDocuments), ...Object.keys(parsedDocuments)])
  allKeys.forEach((key) => {
    const doc = parsedDocuments?.[key] || {}
    const target = emptyDocuments[key] || { fileName: '', fileType: '', fileSize: '', expiryDate: '', previewUrl: '', previewToken: '', publicId: '', assetId: '' }
    
    emptyDocuments[key] = {
      ...target,
      id: doc?.id || doc?.Id || doc?.ID || '',
      documentId: doc?.documentId || doc?.DocumentId || doc?.Id || doc?.ID || '',
      providerId: doc?.providerId || doc?.ProviderId || doc?.ProviderID || '',
      documentKey: key,
      customName: doc?.customName || doc?.CustomName || doc?.DocumentName || doc?.documentName || doc?.DocumentLabel || doc?.documentLabel || doc?.Label || doc?.label || '',
      documentLabel:
        doc?.customName ||
        doc?.CustomName ||
        doc?.DocumentName ||
        doc?.documentName ||
        doc?.DocumentLabel ||
        doc?.documentLabel ||
        doc?.Label ||
        doc?.label ||
        doc?.documentLabel ||
        HRM_ONBOARDING_DOCUMENTS.find((item) => item.key === key)?.label ||
        '',
      fileName: doc?.fileName || doc?.FileName || doc?.filename || '',
      fileType: doc?.fileType || doc?.FileType || doc?.filetype || '',
      fileSize: doc?.fileSize || doc?.FileSize || doc?.filesize || '',
      expiryDate: toInputDate(doc?.expiryDate || doc?.ExpiryDate || ''),
      previewUrl: doc?.previewUrl || doc?.url || doc?.secure_url || doc?.FilePath || doc?.filePath || doc?.filepath || '',
      previewToken: doc?.previewToken || '',
      publicId: doc?.publicId || doc?.public_id || '',
      assetId: doc?.assetId || doc?.asset_id || '',
      documentStatus: doc?.documentStatus || doc?.DocumentStatus || '',
    }
  })

  return emptyDocuments
}

export const hasHRMUploadedDocuments = (documents) => {
  const normalizedDocuments = normalizeHRMDocuments(documents)
  return Object.values(normalizedDocuments).some((doc) => doc?.fileName || doc?.previewUrl)
}

export const normalizeProviderRecord = (provider = {}) => {
  const providerData = safeJsonParse(provider?.ProviderData, {})
  const insurances = safeJsonParse(provider?.Insurances ?? provider?.Insurance, [])
  const facilities = safeJsonParse(provider?.Facilities ?? provider?.Facility, [])
  const addressDetails = safeJsonParse(provider?.AddressDetails || provider?.addressDetails, {})
  const resolvedAddress =
    provider?.Address ||
    provider?.address ||
    provider?.homeAddress ||
    provider?.HomeAddress ||
    addressDetails?.address ||
    addressDetails?.location ||
    providerData?.address ||
    providerData?.location ||
    ''

  return {
    ...provider,
    ID: provider?.ID || provider?.id || provider?.Id || provider?.providerId || provider?.ProviderId || provider?.ProviderID || '',
    id: provider?.id || provider?.ID || provider?.Id || provider?.providerId || provider?.ProviderId || provider?.ProviderID || '',
    Name: getProviderName(provider),
    Gender: provider?.Gender || provider?.gender || 'Male',
    DOB: provider?.DOB || provider?.dob || provider?.DateOfBirth || providerData?.DOB || providerData?.dob || providerData?.DateOfBirth || '',
    Address: resolvedAddress,
    UserId: provider?.UserId || provider?.UserID || '',
    UserName: provider?.UserName || provider?.Username || '',
    Ssn: provider?.ssn || provider?.Ssn || provider?.SSN || providerData?.SSN || '',
    ssn: provider?.ssn || provider?.Ssn || provider?.SSN || providerData?.SSN || '',
    SSN: provider?.SSN || provider?.Ssn || provider?.ssn || providerData?.SSN || '',
    NpiNumber: provider?.npiNumber || provider?.NpiNumber || provider?.NPINumber || providerData?.NPINumber || '',
    npiNumber: provider?.npiNumber || provider?.NpiNumber || provider?.NPINumber || providerData?.NPINumber || '',
    NPINumber: provider?.NPINumber || provider?.NpiNumber || provider?.npiNumber || providerData?.NPINumber || '',
    CaqhId: provider?.caqhId || provider?.CaqhId || provider?.CAQHId || providerData?.CAQHId || '',
    caqhId: provider?.caqhId || provider?.CaqhId || provider?.CAQHId || providerData?.CAQHId || '',
    CAQHId: provider?.CAQHId || provider?.CaqhId || provider?.caqhId || providerData?.CAQHId || '',
    Specialty: provider?.specialty || provider?.Specialty || providerData?.Specialty || '',
    specialty: provider?.specialty || provider?.Specialty || providerData?.Specialty || '',
    MedicareNumber: provider?.medicareNumber || provider?.MedicareNumber || providerData?.MedicareNumber || '',
    medicareNumber: provider?.medicareNumber || provider?.MedicareNumber || providerData?.MedicareNumber || '',
    MedicaidNumber: provider?.medicaidNumber || provider?.MedicaidNumber || providerData?.MedicaidNumber || '',
    medicaidNumber: provider?.medicaidNumber || provider?.MedicaidNumber || providerData?.MedicaidNumber || '',
    Status: provider?.Status || provider?.ActiveStatus || (String(provider?.IsActive).toLowerCase() === 'false' ? 'Inactive' : 'Active'),
    Cap: providerData?.Cap ?? provider?.Cap ?? '',
    ContractStartDate: provider?.ContractStartDate || provider?.contractStartDate || '',
    Insurances: insurances,
    Facilities: facilities,
    Documents: normalizeHRMDocuments(provider?.Documents || provider?.documents),
    AddressDetails: addressDetails,
  }
}

export const normalizeEmployeeKey = (value) => String(value || '').trim().toLowerCase()

export const parseCrudListResponse = (response) => {
  const raw = response?.d ?? response

  if (Array.isArray(raw)) return raw

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed
      if (Array.isArray(parsed?.data?.providers)) return parsed.data.providers
      if (Array.isArray(parsed?.Data?.providers)) return parsed.Data.providers
      if (Array.isArray(parsed?.providers)) return parsed.providers
      if (Array.isArray(parsed?.data)) return parsed.data
      if (Array.isArray(parsed?.Data)) return parsed.Data
      return []
    } catch {
      return []
    }
  }

  if (Array.isArray(raw?.data?.providers)) return raw.data.providers
  if (Array.isArray(raw?.Data?.providers)) return raw.Data.providers
  if (Array.isArray(raw?.providers)) return raw.providers
  if (Array.isArray(raw?.data)) return raw.data
  if (Array.isArray(raw?.Data)) return raw.Data

  return []
}

export const parseUserListResponse = (response) => {
  const outer = response?.d ?? response

  if (Array.isArray(outer)) return outer

  if (typeof outer === 'string') {
    try {
      const parsed = JSON.parse(outer)
      if (Array.isArray(parsed)) return parsed
      if (Array.isArray(parsed?.Users)) return parsed.Users
      if (Array.isArray(parsed?.Data)) return parsed.Data
      return []
    } catch {
      return []
    }
  }

  if (Array.isArray(outer?.Users)) return outer.Users
  if (Array.isArray(outer?.Data)) return outer.Data

  return []
}

export const getMergeKey = (record = {}) => {
  const userId = record.UserId || record.userId || record.UserID
  if (userId) return `user-id:${userId}`

  const username = record.UserName || record.Username || record.username
  if (username) return `username:${normalizeEmployeeKey(username)}`

  const email = record.Email || record.email
  if (email) return `email:${normalizeEmployeeKey(email)}`

  return `record:${record.ID || record.id || record.EmployeeId || Math.random().toString(36).slice(2)}`
}

export const normalizeUserRecord = (user = {}) => {
  let extraData = {}

  try {
    extraData = JSON.parse(user?.ExtraData || user?.extraData || '{}')
  } catch {
    extraData = {}
  }

  return {
    ...user,
    ID: user?.Id ?? user?.ID ?? user?.UserId ?? user?.Username ?? user?.Email ?? `user-${Math.random().toString(36).slice(2, 8)}`,
    UserRecordId: user?.Id ?? user?.ID ?? '',
    UserId: user?.UserId ?? user?.userId ?? user?.Id ?? user?.ID ?? '',
    Name: user?.Name || user?.FullName || extraData?.fullName || user?.Username || user?.Email || 'Unnamed user',
    UserName: user?.Username || user?.UserName || user?.Email || '',
    Email: user?.Email || '',
    Role: user?.Role || user?.role || '',
    PhoneNumber: user?.PhoneNumber || user?.phoneNumber || '',
    Gender: user?.Gender || user?.gender || extraData?.gender || '',
    DOB: user?.DOB || user?.DateOfBirth || extraData?.dob || '',
    Address: user?.Address || user?.Location || '',
    recordType: 'user',
  }
}

export const normalizeEmployeeDetailsRecord = (employee = {}) => ({
  ...employee,
  ID: employee.EmployeeId || employee.employeeId || employee.ID || employee.Id || '',
  EmployeeId: employee.EmployeeId || employee.employeeId || employee.ID || employee.Id || '',
  UserId: employee.UserId || employee.userId || employee.UserID || '',
  UserName: employee.Username || employee.UserName || employee.username || '',
  Username: employee.Username || employee.UserName || employee.username || '',
  Name: employee.EmployeeName || employee.employeeName || employee.Name || '',
  Email: employee.Email || employee.email || '',
  Role: employee.Role || employee.role || '',
  PhoneNumber: employee.PhoneNumber || employee.phoneNumber || '',
  Gender: employee.Gender || employee.gender || 'Male',
  DOB: employee.DOB || employee.dob || '',
  Address: employee.Address || employee.address || employee.Location || employee.location || '',
  ContractStartDate: employee.ContractStartDate || employee.contractStartDate || '',
  Documents: normalizeHRMDocuments(employee.Documents || employee.documents),
  recordType: 'employee',
})

export const normalizeProviderOnboardingRecord = (provider = {}) => ({
  ...normalizeProviderRecord(provider),
  ProviderOnboardingId:
    provider.ProviderOnboardingId ||
    provider.providerOnboardingId ||
    provider.id ||
    provider.ID ||
    provider.Id ||
    provider.OnboardingId ||
    '',
  id:
    provider.ProviderOnboardingId ||
    provider.providerOnboardingId ||
    provider.id ||
    provider.ID ||
    provider.Id ||
    provider.OnboardingId ||
    '',
  UserId: provider.userId || provider.UserId || provider.UserID || '',
  UserName: provider.userName || provider.UserName || provider.Username || provider.username || '',
  Username: provider.username || provider.Username || provider.UserName || provider.userName || '',
  Name: provider.name || provider.Name || provider.ProviderName || provider.providerName || '',
  Gender: provider.gender || provider.Gender || 'Male',
  DOB: provider.dob || provider.DOB || provider.DateOfBirth || safeJsonParse(provider.ProviderData, {})?.DOB || safeJsonParse(provider.ProviderData, {})?.dob || safeJsonParse(provider.ProviderData, {})?.DateOfBirth || '',
  Address:
    provider.address ||
    provider.Address ||
    provider.homeAddress ||
    provider.HomeAddress ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.address ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.location ||
    safeJsonParse(provider.ProviderData, {})?.address ||
    safeJsonParse(provider.ProviderData, {})?.location ||
    '',
  address:
    provider.address ||
    provider.Address ||
    provider.homeAddress ||
    provider.HomeAddress ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.address ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.location ||
    safeJsonParse(provider.ProviderData, {})?.address ||
    safeJsonParse(provider.ProviderData, {})?.location ||
    '',
  HomeAddress:
    provider.homeAddress ||
    provider.HomeAddress ||
    provider.address ||
    provider.Address ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.address ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.location ||
    '',
  homeAddress:
    provider.homeAddress ||
    provider.HomeAddress ||
    provider.address ||
    provider.Address ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.address ||
    safeJsonParse(provider.AddressDetails || provider.addressDetails, {})?.location ||
    '',
  Email: provider.email || provider.Email || '',
  email: provider.email || provider.Email || '',
  PhoneNumber: provider.cellPhoneNumber || provider.CellPhoneNumber || provider.PhoneNumber || '',
  cellPhoneNumber: provider.cellPhoneNumber || provider.CellPhoneNumber || provider.PhoneNumber || '',
  Ssn: provider.ssn || provider.Ssn || provider.SSN || safeJsonParse(provider.ProviderData, {})?.SSN || '',
  ssn: provider.ssn || provider.Ssn || provider.SSN || safeJsonParse(provider.ProviderData, {})?.SSN || '',
  SSN: provider.SSN || provider.Ssn || provider.ssn || safeJsonParse(provider.ProviderData, {})?.SSN || '',
  NpiNumber: provider.npiNumber || provider.NpiNumber || provider.NPINumber || safeJsonParse(provider.ProviderData, {})?.NPINumber || '',
  npiNumber: provider.npiNumber || provider.NpiNumber || provider.NPINumber || safeJsonParse(provider.ProviderData, {})?.NPINumber || '',
  NPINumber: provider.NPINumber || provider.NpiNumber || provider.npiNumber || safeJsonParse(provider.ProviderData, {})?.NPINumber || '',
  CaqhId: provider.caqhId || provider.CaqhId || provider.CAQHId || safeJsonParse(provider.ProviderData, {})?.CAQHId || '',
  caqhId: provider.caqhId || provider.CaqhId || provider.CAQHId || safeJsonParse(provider.ProviderData, {})?.CAQHId || '',
  CAQHId: provider.CAQHId || provider.CaqhId || provider.caqhId || safeJsonParse(provider.ProviderData, {})?.CAQHId || '',
  Specialty: provider.specialty || provider.Specialty || safeJsonParse(provider.ProviderData, {})?.Specialty || '',
  specialty: provider.specialty || provider.Specialty || safeJsonParse(provider.ProviderData, {})?.Specialty || '',
  MedicareNumber: provider.medicareNumber || provider.MedicareNumber || safeJsonParse(provider.ProviderData, {})?.MedicareNumber || '',
  medicareNumber: provider.medicareNumber || provider.MedicareNumber || safeJsonParse(provider.ProviderData, {})?.MedicareNumber || '',
  MedicaidNumber: provider.medicaidNumber || provider.MedicaidNumber || safeJsonParse(provider.ProviderData, {})?.MedicaidNumber || '',
  medicaidNumber: provider.medicaidNumber || provider.MedicaidNumber || safeJsonParse(provider.ProviderData, {})?.MedicaidNumber || '',
  PracticeName: provider.practiceName || provider.PracticeName || '',
  practiceName: provider.practiceName || provider.PracticeName || '',
  PracticeAddress: provider.practiceAddress || provider.PracticeAddress || '',
  practiceAddress: provider.practiceAddress || provider.PracticeAddress || '',
  PracticePhone: provider.practicePhone || provider.PracticePhone || '',
  practicePhone: provider.practicePhone || provider.PracticePhone || '',
  PracticeFax: provider.practiceFax || provider.PracticeFax || '',
  practiceFax: provider.practiceFax || provider.PracticeFax || '',
  OnboardingStatus: provider.onboardingStatus || provider.OnboardingStatus || '',
  onboardingStatus: provider.onboardingStatus || provider.OnboardingStatus || '',
  Status: provider.status || provider.Status || 'Active',
  ContractStartDate: provider.contractStartDate || provider.ContractStartDate || '',
  contractStartDate: provider.contractStartDate || provider.ContractStartDate || '',
  Insurances: safeJsonParse(provider.Insurances || provider.insurances, []),
  Documents: normalizeHRMDocuments(provider.Documents || provider.documents),
  documents: normalizeHRMDocuments(provider.Documents || provider.documents),
  recordType: 'provider',
})

export const mergeDocumentSources = (primaryDocuments, fallbackDocuments) =>
  hasHRMUploadedDocuments(primaryDocuments) ? normalizeHRMDocuments(primaryDocuments) : normalizeHRMDocuments(fallbackDocuments)

export const toCustomDocumentKey = (value = '') => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  return `custom_${normalizedValue || 'miscellaneous'}`
}

export const resolveHRMDocumentKey = (rawDocumentKey = '', index = 0) => {
  const normalizedRawKey = String(rawDocumentKey || '').trim()

  if (normalizedRawKey && (normalizedRawKey.startsWith('custom_') || HRM_ONBOARDING_DOCUMENTS.some((doc) => doc.key === normalizedRawKey))) {
    return normalizedRawKey
  }

  const lowerRawKey = normalizedRawKey.toLowerCase()
  const matchedDocument = HRM_ONBOARDING_DOCUMENTS.find((doc) => {
    const lowerKey = String(doc.key || '').toLowerCase()
    const lowerLabel = String(doc.label || '').toLowerCase()
    return lowerRawKey && (lowerKey === lowerRawKey || lowerLabel === lowerRawKey)
  })

  if (matchedDocument?.key) return matchedDocument.key
  if (normalizedRawKey) return toCustomDocumentKey(normalizedRawKey)

  return HRM_ONBOARDING_DOCUMENTS[index]?.key || ''
}

const hasCustomNamesOrKeys = (candidate) => {
  if (!candidate) return false
  const parsed = safeJsonParse(candidate, {})
  if (Array.isArray(parsed)) {
    return parsed.some((doc) => {
      const key = doc?.documentKey || doc?.key || doc?.DocumentType || doc?.documentType || ''
      const customName = doc?.customName || doc?.CustomName || doc?.DocumentName || doc?.documentName || doc?.DocumentLabel || doc?.documentLabel || doc?.Label || doc?.label || ''
      return key.startsWith('custom_') || customName.trim().length > 0
    })
  }
  if (parsed && typeof parsed === 'object') {
    return Object.keys(parsed).some((key) => {
      if (key.startsWith('custom_')) return true
      const doc = parsed[key] || {}
      const customName = doc?.customName || doc?.CustomName || doc?.DocumentName || doc?.documentName || doc?.DocumentLabel || doc?.documentLabel || doc?.Label || doc?.label || ''
      return customName.trim().length > 0
    })
  }
  return false
}

export const mergeCustomNames = (documentsList, ...dbDocumentsCandidates) => {
  if (!Array.isArray(documentsList) || !documentsList.length) {
    return documentsList
  }

  // Find the first candidate that has custom names/keys
  let dbDocsString = dbDocumentsCandidates.find((candidate) => hasCustomNamesOrKeys(candidate))

  if (!dbDocsString) {
    // Fallback to the first non-empty candidate
    dbDocsString = dbDocumentsCandidates.find((candidate) => {
      if (!candidate) return false
      if (typeof candidate === 'string') {
        return candidate.trim().length > 0
      }
      if (typeof candidate === 'object') {
        return Object.keys(candidate).length > 0
      }
      return false
    })
  }

  const parsedDbDocs = safeJsonParse(dbDocsString, {})
  const dbDocsMap = {}
  
  if (Array.isArray(parsedDbDocs)) {
    parsedDbDocs.forEach((doc, idx) => {
      const key = doc?.documentKey || doc?.key || doc?.DocumentType || doc?.documentType || ''
      const resolvedKey = resolveHRMDocumentKey(key, idx)
      if (resolvedKey) {
        dbDocsMap[resolvedKey] = doc
      }
    })
  } else if (parsedDbDocs && typeof parsedDbDocs === 'object') {
    Object.keys(parsedDbDocs).forEach((key, idx) => {
      const resolvedKey = resolveHRMDocumentKey(key, idx)
      if (resolvedKey) {
        dbDocsMap[resolvedKey] = parsedDbDocs[key]
      }
    })
  }

  return documentsList.map((doc, idx) => {
    const rawKey = doc?.documentKey || doc?.key || doc?.DocumentType || doc?.documentType || ''
    const resolvedKey = resolveHRMDocumentKey(rawKey, idx)
    const dbDoc = dbDocsMap[resolvedKey] || {}
    const customName =
      dbDoc?.customName ||
      dbDoc?.CustomName ||
      dbDoc?.DocumentName ||
      dbDoc?.documentName ||
      dbDoc?.DocumentLabel ||
      dbDoc?.documentLabel ||
      dbDoc?.Label ||
      dbDoc?.label ||
      ''

    return {
      ...doc,
      customName: doc.customName || doc.CustomName || customName || '',
      CustomName: doc.CustomName || doc.customName || customName || '',
    }
  })
}
